// Save-payload logic for editing an already-assigned candidate's mobile number,
// kept framework-free so it can be unit-tested without a React/jest setup (same
// pattern as assessmentConfigDiff / editAssessmentTarget).
//
// Two shapes, because the two assessment kinds store the roster differently:
//   - one-time & corporate -> assessment_assigned_students rows, keyed by assignedId
//   - scheduled            -> student_lists.students_data entries, keyed by email
//
// Both send only the rows that ACTUALLY changed, so an untouched drawer can never
// rewrite a number, and both express "clear this number" as an empty
// mobile_number rather than omitting the row (omitting means "leave alone").

/** Staged edit -> the E.164 string it represents, or '' when cleared. */
const toE164 = (edit) => {
  if (!edit) return ''
  const phone = String(edit.phone || '').trim()
  return phone ? `${edit.countryCode}${phone}` : ''
}

/**
 * Mobile edits for a one-time / corporate assessment.
 *
 * The comparison baseline is the ASSIGNMENT-level number, not what the row
 * displays: a student whose number comes from their profile (mobileFromProfile)
 * has no override yet, so typing that same number is still a real change — it
 * creates the override.
 *
 * @param {Object} edits staged edits keyed by assignedId: { countryCode, phone }
 * @param {Array} students loaded rows: { assignedId, mobile, mobileFromProfile }
 * @returns {Array} [{ assignedId, mobile_number, country_code }]
 */
const buildMobileUpdates = (edits, students) => {
  if (!edits || !Object.keys(edits).length) return []
  const byId = new Map((students || []).map(s => [s.assignedId, s]))

  return Object.entries(edits).reduce((payload, [assignedId, edit]) => {
    const student = byId.get(assignedId)
    if (!student) return payload
    const current = student.mobileFromProfile ? '' : String(student.mobile || '')
    if (toE164(edit) === current) return payload
    payload.push({
      assignedId,
      mobile_number: String(edit?.phone || '').trim(),
      country_code: edit?.countryCode || '',
    })
    return payload
  }, [])
}

/**
 * Mobile edits for a scheduled assessment's roster, keyed by email because
 * roster entries carry no stable id of their own.
 *
 * @param {Object} edits staged edits keyed by email: { countryCode, phone }
 * @param {Array} students roster rows: { email, countryCode, phone }
 * @returns {Array} [{ email, mobile_number, country_code }]
 */
const buildRosterMobileUpdates = (edits, students) => {
  if (!edits || !Object.keys(edits).length) return []
  const byEmail = new Map(
    (students || [])
      .map(s => [String(s.email || s.primary_email || '').trim().toLowerCase(), s])
      .filter(([email]) => email)
  )

  return Object.entries(edits).reduce((payload, [email, edit]) => {
    const key = String(email || '').trim().toLowerCase()
    const student = byEmail.get(key)
    if (!student) return payload
    const current = toE164({ countryCode: student.countryCode, phone: student.phone })
    if (toE164(edit) === current) return payload
    payload.push({
      email: key,
      mobile_number: String(edit?.phone || '').trim(),
      country_code: edit?.countryCode || '',
    })
    return payload
  }, [])
}

module.exports = { buildMobileUpdates, buildRosterMobileUpdates }
